<script setup lang="ts">
  import {
    FlowTypes,
    type FlowConfig,
    InputField,
    UiButton,
    FormFieldContainer,
  } from '@logistiek-zonder-papier/lzp-library'
  import AccordionItem from '@/components/form-wizard/AccordionItem.vue'
  import ColorPicker from '@/components/form-wizard/ColorPicker.vue'

  defineProps<{
    savedCssVariables: Record<string, string>
    cssVariables: Record<string, string>
    colourGroups: { title: string; colours: { css: string; label: string; value: string }[] }[]
    openGroups: Record<string, boolean>
    openFlowGroups: Record<string, boolean>
    flows: FlowConfig[]
    hasFlowColourChanges: boolean
    isSubmitDisabled: boolean
    resetColours: () => void
    resetFlowColours: () => void
  }>()
</script>

<template>
  <div class="colour-container">
    <FormFieldContainer label="Default style" style="width: 100%">
      <template #action-button>
        <UiButton
          label="Reset"
          size="small"
          variant="prominent"
          :disabled="isSubmitDisabled"
          @click="resetColours"
        />
      </template>
      <AccordionItem
        v-for="group in colourGroups"
        :key="group.title"
        :title="group.title"
        v-model:open="openGroups[group.title]!"
      >
        <div v-for="(colour, index) in group.colours" :key="colour.css">
          <FormFieldContainer :label="colour.label" class="color-field">
            <div class="inline">
              <InputField type="text" v-model="colour.value" />
              <ColorPicker v-model="colour.value" />
            </div>
          </FormFieldContainer>
          <br v-if="index < group.colours.length - 1" />
        </div>
      </AccordionItem>
    </FormFieldContainer>

    <FormFieldContainer label="Flow Style">
      <template #action-button>
        <UiButton
          label="Reset"
          size="small"
          variant="prominent"
          :disabled="!hasFlowColourChanges"
          @click="resetFlowColours"
        />
      </template>
      <template v-for="flow in flows" :key="flow.id">
        <AccordionItem
          v-if="flow.type !== FlowTypes.registration"
          :title="flow.label"
          v-model:open="openFlowGroups[flow.id]!"
        >
          <FormFieldContainer label="Button Colour" class="color-field">
            <div class="inline">
              <InputField type="text" v-model="flow.buttonColour" />
              <ColorPicker v-model="flow.buttonColour" />
            </div>
          </FormFieldContainer>

          <FormFieldContainer label="Text Colour" class="color-field">
            <div class="inline">
              <InputField type="text" v-model="flow.textColour" />
              <ColorPicker v-model="flow.textColour" />
            </div>
          </FormFieldContainer>

          <FormFieldContainer label="Activity Indicator Colour" class="color-field">
            <div class="inline">
              <InputField type="text" v-model="flow.activityIndicatorColour" />
              <ColorPicker
                :modelValue="flow.activityIndicatorColour ?? ''"
                @update:modelValue="value => (flow.activityIndicatorColour = value || null)"
              />
            </div>
          </FormFieldContainer>

          <FormFieldContainer label="Activity Indicator Text Colour" class="color-field">
            <div class="inline">
              <InputField type="text" v-model="flow.activityIndicatorTextColour" />
              <ColorPicker
                :modelValue="flow.activityIndicatorTextColour ?? ''"
                @update:modelValue="value => (flow.activityIndicatorTextColour = value || null)"
              />
            </div>
          </FormFieldContainer>
        </AccordionItem>
      </template>
    </FormFieldContainer>
  </div>
</template>

<style scoped lang="css">
  .inline {
    display: flex;
    gap: var(--spacing-s);
    align-items: center;
    justify-content: space-between;
  }

  .colour-container {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-s);
  }
</style>
